import React, { useLayoutEffect, useState, useCallback } from 'react';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import styled from 'styled-components';
import css from '@styled-system/css';
import useSound from '@hooks/useSound';
import { Flex, Label } from 'workspace-core-ui';

const WRAPPER_WIDTH = '47px';
const WRAPPER_HEIGHT = '47px';

// converted using https://www.bayustudio.com/app/svg-encoder/
const generateStarSvg = (
  width = 45,
  height = 45,
  bgColor = 'red',
  outlineColor = 'red',
) =>
  `url("data:image/svg+xml,%3csvg 
  xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${
    width + 1
  } ${height + 1}' %3e%3cdefs%3e%3cstyle%3e.cls-1%7bfill:${encodeURIComponent(
    outlineColor,
  )}%7d%3c/style%3e%3c/defs%3e%3cg id='Layer_2' data-name='Layer 2'%3e%3cg id='Layer_1-2' data-name='Layer 1'%3e%3cg id='Star'%3e%3cpath id='shadow' class='cls-1' d='M46 22.79c1.94-1.93.87-8-1.81-8.36l-.65.07h.12a3.36 3.36 0 0 1 1.81 5.69l-7.39 7.36a3.38 3.38 0 0 0-.94 3l1.75 10.38a3.29 3.29 0 0 1-4.74 3.52L25 39.51a3.16 3.16 0 0 0-3 0l-9.12 4.9a3.29 3.29 0 0 1-4.74-3.52l1.72-10.38a3.35 3.35 0 0 0-.94-3L1.54 20.2a3.35 3.35 0 0 1 1.62-5.64H2.8C.12 14.92-.95 20.3 1 22.23l7.93 7.91c.77.77.17 1.88 0 3L7 43.48A3.29 3.29 0 0 0 11.72 47L22 42.09a3.21 3.21 0 0 1 3 0L35.28 47A3.28 3.28 0 0 0 40 43.48L38.19 33.1c-.19-1.08-.88-2.19-.11-3Z'/%3e%3cpath id='shape' d='M26.43 2.32 31 11.57a3.27 3.27 0 0 0 2.46 1.78l10.21 1.48a3.27 3.27 0 0 1 1.81 5.57l-7.39 7.2a3.27 3.27 0 0 0-.94 2.89l1.75 10.16a3.27 3.27 0 0 1-4.74 3.44L25 39.3a3.27 3.27 0 0 0-3 0l-9.12 4.79a3.26 3.26 0 0 1-4.74-3.44l1.72-10.16a3.24 3.24 0 0 0-.94-2.89l-7.38-7.2a3.27 3.27 0 0 1 1.81-5.57l10.2-1.48A3.24 3.24 0 0 0 16 11.57l4.57-9.25a3.26 3.26 0 0 1 5.86 0Z' style='fill:${encodeURIComponent(
    bgColor,
  )}'/%3e%3cpath id='stroke' class='cls-1' d='M11.34 45a3.77 3.77 0 0 1-3.71-4.4l1.74-10.19A2.75 2.75 0 0 0 8.58 28l-7.39-7.2a3.76 3.76 0 0 1 2.09-6.42l10.2-1.48a2.76 2.76 0 0 0 2.08-1.52l4.57-9.28a3.76 3.76 0 0 1 6.75 0l4.56 9.24a2.79 2.79 0 0 0 2.09 1.52l10.2 1.48a3.76 3.76 0 0 1 2.09 6.42L38.43 28a2.75 2.75 0 0 0-.79 2.45l1.74 10.16a3.77 3.77 0 0 1-5.46 4l-9.13-4.8a2.78 2.78 0 0 0-2.57 0l-9.13 4.8a3.77 3.77 0 0 1-1.75.39ZM23.5 1A2.73 2.73 0 0 0 21 2.54l-4.56 9.25a3.78 3.78 0 0 1-2.83 2.06L3.42 15.33a2.77 2.77 0 0 0-1.53 4.72l7.38 7.19a3.77 3.77 0 0 1 1.09 3.34L8.61 40.74a2.77 2.77 0 0 0 4 2.91l9.12-4.8a3.82 3.82 0 0 1 3.51 0l9.12 4.8a2.76 2.76 0 0 0 4-2.91l-1.71-10.16a3.79 3.79 0 0 1 1.08-3.34l7.39-7.19a2.77 2.77 0 0 0-1.54-4.72l-10.2-1.48a3.78 3.78 0 0 1-2.83-2.06L26 2.54A2.71 2.71 0 0 0 23.5 1Z'/%3e%3c/g%3e%3c/g%3e%3c/g%3e%3c/svg%3e")`;

const Wrapper = styled(Flex)(
  css({
    backgroundRepeat: 'no-repeat',
    backgroundImage: ({ colors }) =>
      generateStarSvg(47, 47, colors.secondaryAccent, colors.foreground),
    height: WRAPPER_HEIGHT,
    width: WRAPPER_WIDTH,
    placeContent: 'center',
    isolation: 'isolate',
  }),
);

const EmphasisMask = styled(Flex)({
  top: 0,
  left: 0,
  position: 'fixed',
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  placeContent: 'center',
  isolation: 'isolate',
  // confetti :) TODO: still a proof of concept
  backgroundRepeat: 'repeat-x',
  transformOrigin: 'center',
  transformBox: 'fill-box',
  backgroundPosition: 'top -10px center',
  backgroundSize: '2000px',
  // TODO: merge this with the defacto confetti component
  // https://stackoverflow.blog/2021/05/31/shipping-confetti-to-stack-overflows-design-system/
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='600' height='90' viewBox='0 0 600 90' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='42' y='-10' width='6' height='10'/%3E%3Crect x='84' y='-10' width='6' height='10'/%3E%3Crect x='126' y='-13' width='5' height='13'/%3E%3Crect x='168' y='-13' width='5' height='13'/%3E%3Crect x='210' y='-10' width='6' height='10'/%3E%3Crect x='252' y='-13' width='5' height='13'/%3E%3Crect x='294' y='-10' width='6' height='10'/%3E%3Crect x='336' y='-13' width='5' height='13'/%3E%3Crect x='378' y='-13' width='5' height='13'/%3E%3Crect x='420' y='-10' width='6' height='10'/%3E%3Crect x='462' y='-10' width='6' height='10'/%3E%3Crect x='504' y='-13' width='5' height='13'/%3E%3Crect x='546' y='-10' width='6' height='10'/%3E%3Cstyle type='text/css'%3E rect %7B opacity: 0; %7D rect:nth-child(1) %7B transform-origin: 45px 5px; transform: rotate(-145deg); animation: blast 700ms infinite ease-out; animation-delay: 88ms; animation-duration: 631ms; %7D rect:nth-child(2) %7B transform-origin: 87px 5px; transform: rotate(164deg); animation: blast 700ms infinite ease-out; animation-delay: 131ms; animation-duration: 442ms; %7D rect:nth-child(3) %7B transform-origin: 128px 6px; transform: rotate(4deg); animation: blast 700ms infinite ease-out; animation-delay: 92ms; animation-duration: 662ms; %7D rect:nth-child(4) %7B transform-origin: 170px 6px; transform: rotate(-175deg); animation: blast 700ms infinite ease-out; animation-delay: 17ms; animation-duration: 593ms; %7D rect:nth-child(5) %7B transform-origin: 213px 5px; transform: rotate(-97deg); animation: blast 700ms infinite ease-out; animation-delay: 122ms; animation-duration: 476ms; %7D rect:nth-child(6) %7B transform-origin: 255px 6px; transform: rotate(57deg); animation: blast 700ms infinite ease-out; animation-delay: 271ms; animation-duration: 381ms; %7D rect:nth-child(7) %7B transform-origin: 297px 5px; transform: rotate(-46deg); animation: blast 700ms infinite ease-out; animation-delay: 131ms; animation-duration: 619ms; %7D rect:nth-child(8) %7B transform-origin: 338px 6px; transform: rotate(-65deg); animation: blast 700ms infinite ease-out; animation-delay: 85ms; animation-duration: 668ms; %7D rect:nth-child(9) %7B transform-origin: 380px 6px; transform: rotate(13deg); animation: blast 700ms infinite ease-out; animation-delay: 128ms; animation-duration: 377ms; %7D rect:nth-child(10) %7B transform-origin: 423px 5px; transform: rotate(176deg); animation: blast 700ms infinite ease-out; animation-delay: 311ms; animation-duration: 508ms; %7D rect:nth-child(11) %7B transform-origin: 465px 5px; transform: rotate(108deg); animation: blast 700ms infinite ease-out; animation-delay: 108ms; animation-duration: 595ms; %7D rect:nth-child(12) %7B transform-origin: 506px 6px; transform: rotate(62deg); animation: blast 700ms infinite ease-out; animation-delay: 105ms; animation-duration: 375ms; %7D rect:nth-child(13) %7B transform-origin: 549px 5px; transform: rotate(16deg); animation: blast 700ms infinite ease-out; animation-delay: 149ms; animation-duration: 491ms; %7D rect:nth-child(odd) %7B fill: %2365BB5C; %7D rect:nth-child(even) %7B z-index: 1; fill: %2333AAFF; %7D rect:nth-child(4n) %7B animation-duration: 1400ms; fill: %23F23B14; %7D rect:nth-child(3n) %7B animation-duration: 1750ms; animation-delay: 700ms; %7D rect:nth-child(4n-7) %7B fill: %232A2F6A; %7D rect:nth-child(6n) %7B fill: %23FBBA23; %7D @keyframes blast %7B from %7B opacity: 0; %7D 20%25 %7B opacity: 1; %7D to %7B transform: translateY(90px); %7D %7D %3C/style%3E%3C/svg%3E%0A")`,

  '@media (prefers-reduced-motion)': {
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='574' height='60' viewBox='0 0 574 60' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect opacity='0.8' x='27.1224' y='20.0458' width='5' height='13' transform='rotate(-139 27.1224 20.0458)' fill='%23F23B14'/%3E%3Crect opacity='0.8' x='118.478' y='7.00201' width='5' height='13' transform='rotate(-38.8114 118.478 7.00201)' fill='%23FBBA23'/%3E%3Crect opacity='0.8' x='504.616' y='25.4479' width='5' height='13' transform='rotate(-60.2734 504.616 25.4479)' fill='%23F23B14'/%3E%3Crect opacity='0.6' x='538.983' y='45.555' width='5' height='13' transform='rotate(16.7826 538.983 45.555)' fill='%232A2F6A'/%3E%3Crect opacity='0.3' x='470.322' y='2.63625' width='5' height='13' transform='rotate(11.295 470.322 2.63625)' fill='%2333AAFF'/%3E%3Crect opacity='0.3' x='190.295' y='4.58138' width='5' height='13' transform='rotate(27.5954 190.295 4.58138)' fill='%23F23B14'/%3E%3Crect opacity='0.8' x='234.303' y='16.3233' width='5' height='13' transform='rotate(-41.8233 234.303 16.3233)' fill='%2365BB5C'/%3E%3Crect opacity='0.6' x='369.702' y='40.9875' width='5' height='13' transform='rotate(-56.419 369.702 40.9875)' fill='%2333AAFF'/%3E%3Crect opacity='0.3' x='402.121' y='31.0848' width='5' height='13' transform='rotate(-17.9234 402.121 31.0848)' fill='%23F23B14'/%3E%3Crect opacity='0.6' x='200.316' y='31.9328' width='5' height='13' transform='rotate(-15.8896 200.316 31.9328)' fill='%232A2F6A'/%3E%3Crect opacity='0.6' x='69.6745' y='23.4725' width='6' height='10' transform='rotate(70.0266 69.6745 23.4725)' fill='%2365BB5C'/%3E%3Crect opacity='0.6' x='291.945' y='7.16931' width='6' height='10' transform='rotate(30.4258 291.945 7.16931)' fill='%23FBBA23'/%3E%3Crect opacity='0.3' x='33.7754' y='38.2208' width='6' height='10' transform='rotate(38.6056 33.7754 38.2208)' fill='%23FBBA23'/%3E%3Crect opacity='0.8' x='109.752' y='31.1743' width='6' height='10' transform='rotate(28.5296 109.752 31.1743)' fill='%2333AAFF'/%3E%3Crect opacity='0.3' x='278.081' y='37.8695' width='6' height='10' transform='rotate(-26.5651 278.081 37.8695)' fill='%23F23B14'/%3E%3Crect opacity='0.8' x='416.294' y='11.5573' width='6' height='10' transform='rotate(-22.8498 416.294 11.5573)' fill='%23FBBA23'/%3E%3Crect opacity='0.3' x='354.667' y='9.32341' width='6' height='10' transform='rotate(17.7506 354.667 9.32341)' fill='%232A2F6A'/%3E%3Crect opacity='0.8' x='532.404' y='16.6372' width='6' height='10' transform='rotate(-75.3432 532.404 16.6372)' fill='%23FBBA23'/%3E%3Crect opacity='0.6' x='460.463' y='39.3557' width='6' height='10' transform='rotate(45.4982 460.463 39.3557)' fill='%2365BB5C'/%3E%3C/svg%3E")`,
  },
});

const Count = styled(Label).attrs({ variant: 'l2' })(
  css({
    color: 'text',
    width: '100%',
  }),
);

interface LevelStarProps {
  count: number;
  isLevellingUp: boolean;
  reducedMotion?: boolean;
  onAnimSequenceEnd?: () => void;
}

const LevelStar = ({
  count = 1,
  isLevellingUp = false,
  reducedMotion = false,
  onAnimSequenceEnd = () => {},
  ...delegated
}: LevelStarProps) => {
  const { playSound } = useSound();
  const [maskIsVisible, setMaskIsVisible] = useState(false);
  const [localCount, setLocalCount] = useState(count);
  const starControls = useAnimation();
  // no exhaustive deps here as we want this cb ref to be stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedOnAnimSequenceEnd = useCallback(onAnimSequenceEnd, []);

  useLayoutEffect(() => {
    async function sequence() {
      await starControls.start({
        x: `calc(-50vw + ${WRAPPER_WIDTH})`,
        y: `calc(50vh - ${WRAPPER_WIDTH})`,
      });
      playSound('LevelUp');
      setMaskIsVisible(true);
      await starControls.start({
        scale: 5,
        transition: { ease: 'easeOut', duration: 0.25 },
      });
      setLocalCount(e => e + 1);
      await starControls.start({
        scale: 8,
        rotate: '20deg',
      });
      await starControls.start({
        rotate: 0,
        scale: 5,
        transition: { ease: 'easeOut', duration: 0.5 },
      });
      await starControls.start({
        scale: 1,
      });
      setMaskIsVisible(false);
      await starControls.start({
        x: 0,
        y: 0,
        rotate: 0,
      });
      memoizedOnAnimSequenceEnd();
    }
    if (isLevellingUp) {
      sequence();
    }
  }, [isLevellingUp, memoizedOnAnimSequenceEnd, starControls]);
  return (
    <>
      <AnimatePresence>
        {maskIsVisible && (
          <EmphasisMask
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>
      <Wrapper
        as={motion.div}
        style={{ originX: WRAPPER_WIDTH / 2, originY: WRAPPER_HEIGHT / 2 }}
        animate={starControls}
        {...delegated}
      >
        <Count>{localCount}</Count>
      </Wrapper>
    </>
  );
};

export default LevelStar;
